<script setup>
import * as permissions from '@/common/permissionConstants';
import { PERSPECTIVES_GUIDE_URL } from '@/common/config';
import BnAvatar from '@/components/BnAvatar.vue';
import BnButton from '@/components/BnButton.vue';
import BnContactSearch from '@/components/BnContactSearch.vue';
import BnDialog from '@/components/BnDialog.vue';
import BnMenu from '@/components/BnMenu.vue';
import BnMenuItem from '@/components/BnMenuItem.vue';
import BnMyAccount from '@/components/account/BnMyAccount.vue';
import BnContactEditDialog from '@/components/contacts/BnContactEditDialog.vue';
import BnPerspectivesSetupDialog from '@/components/perspectives/BnPerspectivesSetupDialog.vue';
import BnTelehealthDock from '@/components/telehealth/BnTelehealthDock.vue';
import BnLensPanel from '@/components/lens/BnLensPanel.vue';
import LensMark from '@/components/lens/LensMark.vue';
import { useLensPanel } from '@/composables/lens/useLensPanel';
import { openHelp } from '@/common/help';
import { IonContent, IonPage } from '@ionic/vue';
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { addRefToBackBtnListiner, goBack, mobileBackNav, removeNativePreviousNavigations, removeRefToBackBtnListiner } from '@/common/nativeBackButton';
import BnFooter from '@/components/BnFooter';
import BnNewIndicator from '@/components/BnNewIndicator.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useDevOnly } from '@/composables/useDevOnly';
import { useUsersnap } from '@/composables/useUsersnap';
import { usePerspectivesSetupReminder } from '@/composables/usePerspectivesSetupReminder';
import { usePerspectivesExtensionDetection } from '@/composables/usePerspectivesExtensionDetection';
import { useMessageInbox } from '@/composables/useMessageInbox';
import { usePortalMessages } from '@/composables/usePortalMessages';
import { usePortalMessageNotifications } from '@/composables/usePortalMessageNotifications';
import { useMessageStream } from '@/composables/useMessageStream';
import messagingApi from '@/common/api/messaging.api';
import { useVersionCheck } from '@/composables/useVersionCheck';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { Capacitor } from '@capacitor/core';
import { onClickOutside } from '@vueuse/core';
import startCase from 'lodash/startCase';

import { addDays, format, parseISO } from 'date-fns';
import { useDisplay } from 'vuetify';
const vuetifyDisplay = useDisplay();

const app = getCurrentInstance();
const isPortal = app.appContext.config.globalProperties?.isPortal;

import { useRoute } from 'vue-router';
const route = useRoute();
const authStore = useAuthStore();
const { isBnSupport, devOnly, isRcmRailPreview } = useDevOnly();
const { isEnabled: featureEnabled } = useFeatureFlags();
const lensPanel = useLensPanel();
const { updateAvailable, reloadForUpdate } = useVersionCheck();

const platform_ios = ref(true);
const drawer = ref(true);
const searchMenu = ref(false);

// Messaging (#5331). Gated on BOTH the account flag and the permissions: a permission can linger
// on a BN_Role after an account turns messaging off (#5326 hides the permission from the tree but
// does not strip it from roles that already hold it), so the flag alone or the permission alone
// would each let the button through in a state where messaging is off.
const { unreadCount: messageUnreadCount, fetchInbox: fetchMessageInbox } = useMessageInbox();
// The portal mints its subscriber token on its own firewall and its own topic; the connection logic
// is the same one (#5333). A session is either the EHR or the portal, never both, so there is one
// right token source at a time.
const { onMessage: onMessageStream } = useMessageStream(isPortal ? { tokenSource: () => messagingApi.getPortalStreamToken() } : {});
let unsubscribeFromMessageStream = null;
const canMessage = computed(() => authStore.messagingEnabled && (authStore.hasPermission(permissions.MESSAGE_WITH_CLIENTS) || authStore.hasPermission(permissions.MESSAGE_WITH_OTHER_USERS)));
const openMessages = () => router.push({ name: 'messages' });

// Portal messaging (#5333). Gated on the account flag alone: a client holds no permissions at all,
// so participation in a conversation is the authority — and that is the server's check, not this
// one. The app-bar Message button above stays EHR-only; the portal's entry point is its nav item.
const { threads: portalThreads, loadThreads: loadPortalThreads } = usePortalMessages();
// The snackbar half of #5334. Kept out of this file so the "when does a client get told" rule is
// testable without mounting the layout; this component only decides that the portal branch is
// where it belongs.
const { handleStreamEvent: notifyPortalMessage } = usePortalMessageNotifications();
const portalMessagingEnabled = computed(() => isPortal && authStore.messagingEnabled);
const portalHasUnread = computed(() => portalThreads.value.some((thread) => thread.unread));

// template refs
const contactMobileSearch = ref(null);
const contactSearch = ref(null);
const bnDialogRef = ref(null);
const myAccountDialog = ref(null);
const navDrawerMain = ref(null);
const contactEditDialog = ref(null);
const ionHeaderRef = ref(null);

import { useStateFlow } from '@/common/workflowEngine/useStateFlow';
import { useNavBar } from '@/composables/useNavBar';
import { usePortalApplication } from '@/composables/usePortalApplication';
const { keepDrawerOpen, secondNavWidth } = useNavBar();
const { startNewApplication } = usePortalApplication();

const mainNavWidth = 80;
const topBarHeight = ref(58);
const isNativePlatform = Capacitor.isNativePlatform();

const navItemsEhr = [
    {
        title: 'Home',
        icon: ['far', 'columns'],
        route: { name: 'dashboard' },
        activeRoutes: ['dashboard'],
    },
    {
        title: 'Contacts',
        icon: ['far', 'users'],
        route: { name: 'contacts' },
        activeRoutes: ['contacts', 'patient', 'practitioner', 'organization'],
    },
    {
        title: 'Calendar',
        icon: ['far', 'calendar-alt'],
        route: { name: 'calendar' },
        activeRoutes: ['calendar'],
        keepOpen: true,
    },
    {
        title: 'General log',
        icon: ['far', 'cabinet-filing'],
        route: { name: 'general-log' },
        activeRoutes: ['general-log'],
        keepOpen: true,
        requiredPermission: permissions.VIEW_GENERAL_LOG,
    },
    {
        title: 'Admissions',
        icon: ['far', 'clipboard-user'],
        route: { name: 'admissions' },
        activeRoutes: ['admissions'],
        keepOpen: true,
        featureFlag: 'admissions',
        requiredPermission: permissions.ADMISSIONS_ACCESS,
    },
    {
        title: 'Reporting',
        icon: ['far', 'analytics'],
        route: { name: 'reports' },
        activeRoutes: ['reports'],
        keepOpen: true,
    },
    {
        title: 'Lens',
        icon: ['far', 'wand-magic-sparkles'],
        route: { name: 'lens' },
        activeRoutes: ['lens', 'lens-builder', 'lens-view'],
        keepOpen: true,
        featureFlag: 'lens',
        requiredPermission: permissions.LENS_VIEW,
    },
    {
        title: 'Labs',
        icon: ['far', 'vials'],
        route: { name: 'labs' },
        activeRoutes: ['labs'],
        keepOpen: true,
        devOnly: true,
    },
    {
        title: 'Financial',
        icon: ['far', 'file-invoice-dollar'],
        route: { name: 'financial-page' },
        // Ledger lives under Financial → Account activity (and patient Financial), not as a peer module.
        activeRoutes: ['financial-page', 'ledger'],
        keepOpen: false,
        requiredPermission: permissions.VIEW_CLAIMS,
    },
    {
        title: 'Payments',
        icon: ['far', 'credit-card'],
        route: { name: 'payments' },
        activeRoutes: ['payments'],
        requiredPermission: permissions.ACCEPT_PAYMENTS,
        keepOpen: false,
        rcmRailOnly: true,
    },
    {
        title: 'eMAR',
        icon: ['far', 'pills'],
        route: { name: 'medication-pass' },
        activeRoutes: ['medication-pass'],
        keepOpen: false,
        requiredPermission: permissions.VIEW_MED_LIST,
        accountFlag: 'medicationAdministrationEnabled',
        featureFlag: 'emar',
    },
    {
        title: 'Settings',
        icon: ['far', 'cog'],
        route: { name: 'settings' },
        activeRoutes: ['settings'],
        keepOpen: true,
    },
    {
        title: 'Messages',
        icon: ['far', 'comment-alt'],
        route: { name: 'messages' },
        activeRoutes: ['messages'],
        devOnly: true,
        // Same gate the app-bar button and the page body use. Without it a user who cannot message
        // still gets a nav entry that leads to a "not available" alert (#5331).
        requiresMessaging: true,
    },
];

const navItemsPortal = [
    {
        title: 'Home',
        icon: ['fas', 'columns'],
        route: { name: 'dashboard' },
    },
    {
        title: 'Files',
        icon: ['far', 'hdd'],
        route: { name: 'files' },
    },
    {
        title: 'Resources',
        icon: ['far', 'link'],
        route: { name: 'resources' },
    },
    {
        title: 'Messages',
        icon: ['far', 'comment-alt'],
        route: { name: 'messages' },
        activeRoutes: ['messages'],
        // Hidden entirely rather than shown and refused when the account has messaging off — the
        // page itself explains the state to anyone who reaches it by URL (#5333).
        requiresMessaging: true,
        // Unread is a dot on this item rather than a second app-bar button: the portal's app bar has
        // no Message button, and the nav is where a client already looks for their sections.
        unreadIndicator: true,
    },
];

const navDrawerContent = ref(null);
const twoNavs = ref(false);

let observer;

const setActiveAgency = async () => {
    let timeout = setTimeout(() => {
        setActiveBrand();
        clearTimeout(timeout);
        timeout = null;
    }, 250);
};

const updateHeaderHeight = () => {
    if (ionHeaderRef.value) {
        nextTick(() => {
            // Get the actual header element
            const headerEl = ionHeaderRef.value.$el;
            const headerHeight = headerEl.offsetHeight;

            // For iPad, we need to account for the safe area
            if (platform_ios.value && Capacitor.isNativePlatform() && (window.innerWidth >= 768 || window.innerHeight >= 768)) {
                // iPad detection
                // Get the computed style to check if there's a safe area inset
                const computedStyle = window.getComputedStyle(headerEl);
                const paddingTop = parseInt(computedStyle.paddingTop, 10) || 0;

                // Use either the calculated height or add the safe area padding if detected
                topBarHeight.value = headerHeight > 0 ? headerHeight : topBarHeight.value + paddingTop;
            } else if (headerHeight > 0) {
                // For non-iPad devices, just use the measured height
                topBarHeight.value = headerHeight;
            }
        });
    }
};

const handleOrientationChange = () => {
    setTimeout(updateHeaderHeight, 300);
};

const setupOrientationListener = () => {
    if (platform_ios.value && Capacitor.isNativePlatform()) {
        window.addEventListener('orientationchange', handleOrientationChange);
    }
};

const hasPerspectivesAccess = computed(() => {
    return authStore.hasPermission(permissions.PERSPECTIVES_ACCESS);
});

// Own detection instance from the same shared composable the reminder dialog uses below, so the
// app-bar button and the dialog use the same polling logic and production gate — both wait out
// the same retry window before concluding the extension is genuinely absent. They're separate
// timers, so their verdicts can land up to one poll interval apart (#5823).
const { hasExtension: hasPerspectivesExtension, stop: stopPerspectivesExtensionDetection } = usePerspectivesExtensionDetection({
    hasAccess: hasPerspectivesAccess,
});

const {
    shouldShow: showPerspectivesSetup,
    dismiss: dismissPerspectivesSetup,
    stop: stopPerspectivesSetupReminder,
} = usePerspectivesSetupReminder({
    hasAccess: hasPerspectivesAccess,
    userId: authStore.user?.apiUser?.id,
});

// Only a close dismisses: dismiss() records a 3-day snooze whatever it's given, so an open
// emission must not reach it.
const onPerspectivesSetupUpdate = (open) => {
    if (!open) dismissPerspectivesSetup();
};

const accountReadOnlyUntil = computed(() => {
    if (!authStore.account?.canceledDate) return null;
    const canceledDate = parseISO(authStore.account.canceledDate);
    return format(addDays(canceledDate, 90), 'MMM d, yyyy');
});

const installPerspectives = () => {
    if (hasPerspectivesAccess.value) {
        // noopener: the vendor page must not get a handle back to this authenticated tab — the same
        // guard the setup dialog's own download link uses.
        window.open(PERSPECTIVES_GUIDE_URL, '_blank', 'noopener,noreferrer');
    }
};

onMounted(() => {
    const { initializeUsersnap } = useUsersnap();
    // this.savingGetter = route.meta.savingGetter || 'isSaving';

    if (vuetifyDisplay.smAndDown.value) {
        drawer.value = false;
    }

    if (!vuetifyDisplay.xs.value && !window.Cypress) {
        initializeUsersnap();
    }

    // Populate the unread badge. fetchInbox swallows its own failures, so a messaging outage
    // degrades to no dot rather than blocking the layout from mounting.
    if (!isPortal && canMessage.value) {
        fetchMessageInbox();

        // The unread dot updates live. This layout is the right owner: it is mounted for the whole
        // session, so the stream survives navigating between pages, and holding the subscription
        // here means a user who never opens messaging still gets the badge without the Messages
        // page ever having been visited. The event carries only ids — the inbox re-reads through
        // its own policy-gated query (#5338).
        unsubscribeFromMessageStream = onMessageStream((event) => {
            // `stream.resumed` is synthetic — the composable emits it after a RE-connect, because a
            // gap may have swallowed events and the gateway replays nothing (#5432). The answer is
            // the same re-read a real event triggers, since the payload never carried anything to
            // apply.
            if (event?.type === 'message.created' || event?.type === 'stream.resumed') fetchMessageInbox();
        });
    } else if (portalMessagingEnabled.value) {
        // The portal equivalent, and the same reasoning: the layout is mounted for the whole
        // session, so the client's unread dot stays current without their ever having opened the
        // Messages page. Both calls swallow their own failures, so a messaging outage degrades to
        // no dot (#5333). One branch or the other, never both — the two subscriptions share one
        // handle, and a session is either the EHR or the portal.
        loadPortalThreads();

        unsubscribeFromMessageStream = onMessageStream((event) => {
            if (event?.type === 'message.created' || event?.type === 'stream.resumed') loadPortalThreads();
            // Alongside the unread dot, never instead of it (#5334). A snackbar is transient — a
            // client who steps away misses it entirely — so the indicator is what actually carries
            // the state and the snackbar is only what makes it noticeable while they are here.
            //
            // The reconnect refreshes the list but raises NO snackbar: "you reconnected" is not news
            // about a message, and it names no thread to open (#5432).
            if (event?.type !== 'stream.resumed') notifyPortalMessage(event);
        });
    }

    window.addEventListener('defaultLayout.setActiveAgency', setActiveAgency);
    window.addEventListener('resize', updateHeaderHeight);

    observer = new MutationObserver(() => {
        twoNavs.value = !!navDrawerContent.value?.children?.length;
    });

    observer.observe(navDrawerContent.value, { childList: true, subtree: true });

    // Measure header height after mount
    setupOrientationListener();
    updateHeaderHeight();

    // Load related persons for portal users
    if (isPortal) {
        loadRelatedPersons();
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('defaultLayout.setActiveAgency', setActiveAgency);
    window.removeEventListener('resize', updateHeaderHeight);
    window.removeEventListener('orientationchange', handleOrientationChange);
    observer?.disconnect();
    stopPerspectivesExtensionDetection();
    stopPerspectivesSetupReminder();
    unsubscribeFromMessageStream?.();
    unsubscribeFromMessageStream = null;
});

// when clicking outside mobile search bar close search bar,  Need to set timeout for code to execute when opening a patient
onClickOutside(contactMobileSearch, () => {
    let timeout = setTimeout(() => {
        searchMenu.value = false;
        clearTimeout(timeout);
        timeout = null;
    }, 250);
});

const brandName = computed(() => {
    return authStore.brand?.name || authStore.organizationAccount?.name;
});

const location = computed(() => {
    return authStore.brand?.location;
});

const brandLogo = computed(() => {
    return authStore.brand?.logo || authStore.account.logo;
});

const userBrands = computed(() => {
    return authStore.userAgencies;
});

// The active location's own closed flag, for the badge in the top bar.
const activeLocationClosed = computed(() => authStore.brand?.location?.closed === true);

// Only the practitioner's primary location earns the "update it in user management" tooltip —
// that advice is meaningless on a closed location they merely switched into for the session.
const primaryLocationId = computed(() => authStore.practitioner?.primaryLocation?.id);
const isPrimaryLocation = (locationId) => !!locationId && locationId === primaryLocationId.value;
const activeLocationIsPrimary = computed(() => isPrimaryLocation(authStore.brand?.location?.id));

const multipleBrandsLocations = computed(() => {
    return userBrands.value?.length > 1 || userBrands.value.some((item) => item.locations?.length > 1);
});

// Load related persons from store
const loadRelatedPersons = async () => {
    if (!isPortal) return;
    await authStore.loadRelatedPersons();
};

const viewingSelfDisplay = computed(() => {
    if (!authStore.portalViewingPatient) return 'Myself';
    return authStore.portalViewingPatient?.id === authStore.patient?.id ? 'Myself' : authStore.portalViewingPatient.firstName || 'Unknown';
});

const routes = computed(() => {
    let endRoutes = [];
    let computedRoutes = [];
    let routes = route.matched.filter((r) => r.name || r.meta.label || r.meta.path);

    routes.forEach((r) => {
        if (r.meta.path) {
            r.meta.path.forEach((val) => endRoutes.push(router.resolve({ name: val }).route));
        }

        endRoutes.push(r);
    });

    endRoutes.forEach((route) => {
        computedRoutes.push({
            text: getTitle(route),
            href: route.path,
            disabled: false,
            rawRoute: route,
        });
    });

    // disable last entry
    computedRoutes[computedRoutes.length - 1].disabled = true;

    return computedRoutes;
});

const isSaving = computed(() => {
    return false;
    // return this.$store.getters[this.savingGetter];
});

const navItems = computed(() => {
    if (isPortal) {
        return navItemsPortal.filter((item) => !item.requiresMessaging || authStore.messagingEnabled);
    } else {
        return navItemsEhr.filter((item) => {
            // A feature the account has not switched on has no entry, whatever the user may do.
            if (item.accountFlag && !authStore.account?.[item.accountFlag]) return false;
            if (item.requiresMessaging && !canMessage.value) return false;
            if (item.requiredPermission) return authStore.hasPermission(item.requiredPermission);
            return true;
        });
    }
});

const browserTitle = computed(() => {
    let routeArray = Array.from(routes).reverse();
    let titleAddArray = routeArray.filter((i) => i.rawRoute.meta.includeInTitle).map((i) => i.rawRoute.meta.label || startCase(i.rawRoute.name));
    titleAddArray.unshift(getTitle());
    let newTitle = titleAddArray.length ? titleAddArray.join(' · ') : '';
    document.title = newTitle + ' | BestNotes';
    return newTitle;
});

const isNavItemActive = (item) => {
    if (!item.activeRoutes) return false;

    // Check if any matched route names are in the activeRoutes array
    return route.matched.some((matchedRoute) => {
        return item.activeRoutes.includes(matchedRoute.name);
    });
};

const avatarResource = computed(() => {
    return {
        resourceType: 'Organization',
        logo: brandLogo.value,
        name: brandName.value,
    };
});

/**
 * Watchers
 */
watch(
    () => vuetifyDisplay.smAndDown,
    (newVal) => {
        drawer.value = !newVal;
    },
);

watch(drawer, () => {
    if (keepDrawerOpen.value) {
        drawer.value = true;
        keepDrawerOpen.value = false;
        navDrawerMain.value.isActive = true;
    }
});

/**
 * Methods
 */

const { showUsersnap } = useUsersnap();

const signOut = async () => {
    const options = {
        header: 'Sign out',
        message: 'Are you sure you want to sign out?',
        confirm: 'Sign out',
        cancel: 'Cancel',
        width: 400,
    };
    bnDialogRef.value.show(options).then(async (confirm) => {
        if (!confirm) return;

        try {
            if (!isPortal) {
                const { resetApplicationState } = useStateFlow();
                await resetApplicationState({ practitioner: authStore.practitioner });
            }
        } catch (error) {
            console.warn('Error during sign out process:', error);
        } finally {
            // Always navigate to logout, regardless of success or failure
            router.push({ name: 'logout' });
        }
    });
};

const setActiveBrand = (brandLocation) => {
    authStore.setBrand(brandLocation);
};
const addContact = (item) => {
    const options = {
        header: 'Add contact',
        persistent: true,
    };
    const props = {
        add: item,
    };
    addRefToBackBtnListiner(contactEditDialog, 'close', 'contactEditDialog');
    contactEditDialog.value.show(options, props).then(async (resource) => {
        removeRefToBackBtnListiner('contactEditDialog');
        if (resource) {
            router.push({ name: resource.resourceType.toLowerCase(), params: { id: resource.id, activeTab: 'contact' } });
        }
    });
    contactSearch.value.search = '';
    searchMenu.value = false;
};

const openContact = (item) => {
    searchMenu.value = false;
    router.push({ name: item.resourceType.toLowerCase(), params: { id: item.id } });
};

const getTitle = (r) => {
    if (!r) {
        r = route;
    }

    return r.meta.label || startCase(r.name);
};

const routeToPage = (item) => {
    // only navigate if the route has actually changed (avoid duplicate navigation error)
    if (item.route.name !== route.name) {
        router.push(item.route);

        if (!item.keepOpen && vuetifyDisplay.smAndDown.value === true) {
            drawer.value = false;
        }
    }
    removeNativePreviousNavigations();
};
const mobileBackNavForRelatedContacts = () => {
    goBack();
};
const filterOutPayers = (e) => {
    if (e.resourceType === 'Organization') {
        return !e?.type?.some((f) => f.coding.some((i) => i.code === 'pay'));
    }
    return true;
};

const setDrawer = () => {
    drawer.value = !drawer.value;
};

const closeDrawer = () => {
    if (vuetifyDisplay.smAndDown.value) {
        drawer.value = false;
    }
};
</script>

<template>
    <IonPage>
        <div style="display: contents">
            <v-navigation-drawer
                ref="navDrawerMain"
                v-model="drawer"
                order="2"
                :absolute="!vuetifyDisplay.smAndDown.value"
                theme="dark"
                :permanent="!vuetifyDisplay.smAndDown.value"
                :temporary="vuetifyDisplay.smAndDown.value"
                rail
                :rail-width="mainNavWidth"
                class="pt-1"
                :class="{ openMainDrawer: drawer }"
                :style="`z-index: 1100; overflow-x: hidden; top: ${topBarHeight}px;`"
                disable-route-watcher
            >
                <v-list two-line class="text-center">
                    <v-list-item
                        v-for="item in navItems.filter(
                            (e) =>
                                (e.devOnly ? devOnly() : true) &&
                                (e.rcmRailOnly ? isRcmRailPreview() : true) &&
                                (e.bnSupportOnly ? isBnSupport() : true) &&
                                (e.featureFlag ? featureEnabled(e.featureFlag) : true),
                        )"
                        :key="item.title"
                        :active="isNavItemActive(item)"
                        class="px-0 justify-center"
                        :class="{ 'bn-dev-only': item.devOnly || item.rcmRailOnly }"
                        active-class="border-left"
                        color="accent"
                        @click="routeToPage(item)"
                    >
                        <!-- The dot rides the icon rather than sitting beside the label, so the
                             nav's fixed rail width is unchanged however long the title. -->
                        <v-badge v-if="item.unreadIndicator" :model-value="portalHasUnread" color="accent" dot data-cy="portalMessagesUnreadBadge">
                            <font-awesome-icon :icon="item.icon" size="lg" style="color: white" />
                        </v-badge>
                        <font-awesome-icon v-else :icon="item.icon" size="lg" style="color: white" />
                        <v-list-item-title class="text-caption text-white">{{ item.title }}</v-list-item-title>
                        <!-- Corner badge: the 80px rail truncates the title when the chip sits inline. -->
                        <BnNewIndicator v-if="item.featureFlag" :tag="item.featureFlag" compact class="position-absolute" style="top: 4px; right: 6px" />
                    </v-list-item>
                </v-list>
            </v-navigation-drawer>

            <v-navigation-drawer
                ref="navDrawer"
                v-model="drawer"
                order="2"
                :permanent="!vuetifyDisplay.smAndDown.value"
                :temporary="vuetifyDisplay.smAndDown.value"
                :scrim="false"
                :absolute="!vuetifyDisplay.smAndDown.value"
                disable-route-watcher
                :width="twoNavs ? secondNavWidth : 0"
                :style="`z-index: 1100; overflow-x: hidden; top: ${topBarHeight}px;`"
                :class="{ ios_header_padding: platform_ios, openSecondaryDrawer: drawer, 'd-none': !twoNavs }"
            >
                <v-row class="flex-nowrap" no-gutters>
                    <v-list class="d-flex flex-column scroll-on">
                        <div id="navDrawer" ref="navDrawerContent" @click="closeDrawer"></div>
                    </v-list>
                </v-row>
            </v-navigation-drawer>

            <ion-header ref="ionHeaderRef" class="ion-no-border">
                <ion-toolbar>
                    <v-app-bar order="1" density="comfortable" flat color="#f5f5f5" short style="z-index: 110">
                        <!-- Small devices - display navigation hamburger -->
                        <div :class="isNativePlatform ? 'ml-1' : 'ml-3'">
                            <BnButton v-if="vuetifyDisplay.smAndDown.value" icon="bars" @click="setDrawer" />
                            <!-- Large devices - always display search -->
                            <div v-if="!isPortal && !vuetifyDisplay.smAndDown.value" style="width: 300px" class="mr-2 flex-grow-1">
                                <BnContactSearch
                                    ref="contactSearch"
                                    variant="outlined"
                                    flat
                                    default-query="recent"
                                    density="compact"
                                    label=""
                                    placeholder="Search/add contacts"
                                    :filter="filterOutPayers"
                                    data-cy="primaryContactSearch"
                                    @add-new-clicked="addContact"
                                    @item-clicked="openContact"
                                />
                            </div>
                        </div>
                        <!--  Mobile Back Navigation for Related Contacts -->
                        <div v-if="isNativePlatform && mobileBackNav.status" class="d-flex align-center text-truncate pl-1 pr-3">
                            <BnButton icon="arrow-left" size="lg" :style="{ color: '#000 !important' }" @click="mobileBackNavForRelatedContacts" />
                        </div>
                        <!-- All devices - Show active brand with selector if required -->
                        <div class="d-flex align-center text-truncate">
                            <BnAvatar v-if="vuetifyDisplay.smAndUp.value" :resource="avatarResource" no-badge is-public size="36"></BnAvatar>
                            <div class="text-truncate ml-2" data-cy="brandName">
                                {{ brandName }}
                                <div v-if="location" class="text-caption mt-n1 d-flex align-center">
                                    <font-awesome-icon :icon="['far', 'map-marker-alt']" />
                                    <span class="ml-1 text-truncate">{{ location.display || getDisplay(location) }}</span>
                                    <v-chip v-if="activeLocationClosed" class="ml-2" color="warning" data-cy="activeLocationClosedChip">
                                        Closed
                                        <v-tooltip
                                            v-if="activeLocationIsPrimary"
                                            activator="parent"
                                            location="bottom"
                                            text="This location is closed. Update your primary location in user management."
                                        />
                                    </v-chip>
                                </div>
                            </div>
                            <template v-if="multipleBrandsLocations">
                                <BnMenu offset-y left :icon="['fas', 'caret-down']" icon-size="lg" class="ml-2">
                                    <template v-for="brand in userBrands" :key="brand.id">
                                        <BnMenuItem class="py-0" disabled :label="brand.name || getDisplay(brand)" icon="building" />
                                        <template v-for="brandLocation in brand.locations" :key="brandLocation.id">
                                            <BnMenuItem
                                                class="pl-6 py-0"
                                                icon="map-marker-alt"
                                                :label="brandLocation.display || getDisplay(brandLocation)"
                                                @click="setActiveBrand({ id: brand.id, name: brand.name, location: brandLocation })"
                                            >
                                                <template v-if="brandLocation.closed" #append>
                                                    <v-chip color="warning" data-cy="switcherLocationClosedChip">
                                                        Closed
                                                        <v-tooltip
                                                            v-if="isPrimaryLocation(brandLocation.id)"
                                                            activator="parent"
                                                            location="bottom"
                                                            text="This location is closed. Update your primary location in user management."
                                                        />
                                                    </v-chip>
                                                </template>
                                            </BnMenuItem>
                                        </template>
                                    </template>
                                </BnMenu>
                            </template>
                        </div>
                        <!-- Viewing person selector for portal -->
                        <v-menu v-if="isPortal && authStore.portalViewingPatient" offset-y>
                            <template #activator="{ props }">
                                <v-btn v-bind="props" variant="text" class="text-capitalize">
                                    <span class="text-caption text-grey mr-2">Viewing:</span>
                                    <BnAvatar :resource="authStore.portalViewingPatient" no-badge is-public size="32" class="mr-2" />
                                    <span>{{ viewingSelfDisplay }}</span>
                                    <font-awesome-icon :icon="['fas', 'caret-down']" class="ml-2" />
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item v-for="person in authStore.portalPatientItems" :key="person.patient?.id" @click="authStore.setPortalViewingPatientId(person.patient.id)">
                                    <template #prepend>
                                        <BnAvatar :resource="person.patient" no-badge is-public size="32" class="mr-3" />
                                    </template>
                                    <v-list-item-title>{{ person.display }}</v-list-item-title>
                                </v-list-item>
                                <v-divider class="my-2" />
                                <v-list-item @click="startNewApplication">
                                    <template #prepend>
                                        <font-awesome-icon :icon="['far', 'plus']" size="lg" class="mr-3 ml-1" />
                                    </template>
                                    <v-list-item-title>New application for someone else</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <v-spacer></v-spacer>

                        <!-- Small devices - move search to button icon -->
                        <template v-if="!isPortal && vuetifyDisplay.smAndDown.value">
                            <v-menu v-model="searchMenu" :close-on-content-click="false" offset-y min-width="320">
                                <template #activator="{ props }">
                                    <v-btn v-bind="props" icon>
                                        <font-awesome-icon :icon="['far', 'search']" size="lg" />
                                    </v-btn>
                                </template>
                                <v-card ref="contactMobileSearch" min-width="300" class="mx-auto fill-width" variant="outlined">
                                    <BnContactSearch
                                        ref="contactSearch"
                                        autofocus
                                        default-query="recent"
                                        density="compact"
                                        variant="outlined"
                                        :filter="filterOutPayers"
                                        label=""
                                        placeholder="Search contacts"
                                        data-cy="primaryContactSearch"
                                        @add-new-clicked="addContact"
                                        @item-clicked="openContact"
                                    />
                                </v-card>
                            </v-menu>
                        </template>
                        <v-tooltip v-if="canMessage" location="bottom">
                            <template #activator="{ props: messageTooltipProps }">
                                <v-btn v-bind="messageTooltipProps" icon class="ml-2" data-cy="messageButton" @click="openMessages">
                                    <!-- `primary`, matching the inbox: `warning` is reserved for the
                                         archived/closed state, and unread is not a warning. -->
                                    <v-badge :model-value="messageUnreadCount > 0" color="primary" dot data-cy="messageUnreadBadge">
                                        <font-awesome-icon :icon="['far', 'comments']" size="lg" />
                                    </v-badge>
                                </v-btn>
                            </template>
                            {{ messageUnreadCount > 0 ? `${messageUnreadCount} unread message${messageUnreadCount === 1 ? '' : 's'}` : 'Messages' }}
                        </v-tooltip>
                        <v-btn v-if="updateAvailable" color="primary" variant="outlined" class="ml-2 text-capitalize" @click="reloadForUpdate">
                            <font-awesome-icon :icon="['far', 'arrow-rotate-right']" />
                            <span class="ml-2">Update available - Refresh</span>
                        </v-btn>
                        <v-tooltip v-else-if="authStore.account?.suspendedDate" location="bottom">
                            <template #activator="{ props: tooltipProps }">
                                <v-chip v-bind="tooltipProps" color="warning" size="large" class="ml-2">
                                    <font-awesome-icon :icon="['far', 'pause-circle']" />
                                    <span class="ml-2">Account suspended</span>
                                    <font-awesome-icon :icon="['far', 'circle-info']" class="ml-2" />
                                </v-chip>
                            </template>
                            This account has been suspended. You have read-only access until the suspension is removed. Contact support for assistance.
                        </v-tooltip>
                        <v-tooltip v-else-if="authStore.account?.canceledDate" location="bottom">
                            <template #activator="{ props: tooltipProps }">
                                <v-chip v-bind="tooltipProps" color="error" size="large" class="ml-2">
                                    <font-awesome-icon :icon="['far', 'ban']" />
                                    <span class="ml-2">Account canceled</span>
                                    <font-awesome-icon :icon="['far', 'circle-info']" class="ml-2" />
                                </v-chip>
                            </template>
                            Account has been canceled. You have read-only access. Contact support for assistance.
                        </v-tooltip>
                        <v-btn v-else-if="!hasPerspectivesExtension" color="primary" variant="outlined" class="ml-2 text-capitalize" @click="installPerspectives">
                            <font-awesome-icon :icon="['far', 'puzzle-piece']" />
                            <span class="ml-2">Install Perspectives AI</span>
                        </v-btn>
                        <v-menu>
                            <template #activator="{ props }">
                                <v-btn v-bind="props" rounded large class="ml-2 text-capitalize" text :icon="vuetifyDisplay.smAndDown.value" data-cy="userMenu">
                                    <span v-if="!vuetifyDisplay.smAndDown.value" class="mr-2"> Hi, {{ authStore.firstName }} </span>
                                    <BnAvatar :resource="authStore.aidboxPersonResource" no-badge is-public size="36" />
                                </v-btn>
                            </template>

                            <v-card class="mx-auto" outlined>
                                <picture>
                                    <source srcset="../../assets/img/docks_344x200.webp" type="image/webp" />
                                    <img src="../../assets/img/docks_344x200.jpg" alt="Docks" style="width: 344px; height: 200px" />
                                </picture>
                                <v-card-title class="mt-n16" style="color: white">{{ authStore.fullName || 'Unnamed User' }}</v-card-title>

                                <v-spacer></v-spacer>

                                <v-list>
                                    <v-list-item @click="$refs.myAccountDialog.show()">
                                        <template #prepend>
                                            <font-awesome-icon class="mr-2" :icon="['far', 'user-cog']" />
                                        </template>
                                        <v-list-item-title>My BestNotes account</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item v-if="!isPortal && featureEnabled('help-center')" data-cy="helpMenuItem" @click="openHelp()">
                                        <template #prepend>
                                            <font-awesome-icon class="mr-2" :icon="['far', 'circle-question']" />
                                        </template>
                                        <v-list-item-title>Help<BnNewIndicator tag="help-center" /></v-list-item-title>
                                    </v-list-item>

                                    <v-list-item v-if="authStore.showSwitchAccount" @click="router.push({ name: 'account-selection' })">
                                        <template #prepend>
                                            <font-awesome-icon class="mr-2" :icon="['far', 'random']" />
                                        </template>
                                        <v-list-item-title>Switch account</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item @click="showUsersnap">
                                        <template #prepend>
                                            <font-awesome-icon class="mr-2" :icon="['far', 'bullhorn']" />
                                        </template>
                                        <v-list-item-title>Feedback</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item data-cy="lockApplication" @click="router.push({ name: 'lockout' })">
                                        <template #prepend>
                                            <font-awesome-icon class="mr-2" :icon="['far', 'user-lock']" />
                                        </template>
                                        <v-list-item-title>Lock application</v-list-item-title>
                                    </v-list-item>
                                </v-list>

                                <v-card-actions class="p-2 d-flex flex-row-reverse">
                                    <v-btn color="primary" variant="outlined" @click="signOut">Sign out</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-menu>
                        <v-btn
                            v-if="!isPortal && featureEnabled('lens-panel')"
                            icon
                            variant="text"
                            class="ml-1"
                            :aria-pressed="lensPanel.open.value"
                            aria-label="Lens"
                            title="Lens"
                            data-cy="lensPanelToggle"
                            @click="lensPanel.toggle()"
                        >
                            <LensMark :size="36" />
                            <BnNewIndicator tag="lens-panel" compact class="position-absolute" style="top: -4px; right: -4px" />
                        </v-btn>

                        <div style="position: absolute; top: 74px; right: 16px">
                            <v-chip v-if="false" color="error">
                                <font-awesome-icon :icon="['fal', 'exclamation-triangle']"></font-awesome-icon>
                                <span class="ml-2">Retry has timed out after 5 minutes.</span>
                                <v-btn ref="retrySaves" text color="white" class="ml-1" @click="retrySaves">Retry</v-btn>
                            </v-chip>

                            <!--                        <v-chip v-else-if="hasNetworkIssues" color="error">-->
                            <!--                            <font-awesome-icon :icon="['fal', 'exclamation-triangle']"></font-awesome-icon>-->
                            <!--                            <span class="ml-2">Network Connectivity Issues, Retrying...</span>-->
                            <!--                        </v-chip>-->

                            <v-chip v-else-if="isSaving">
                                <font-awesome-icon :icon="['far', 'upload']"></font-awesome-icon>
                                <span class="ml-2">Saving...</span>
                            </v-chip>

                            <!--                        <v-chip v-else>-->
                            <!--                            <font-awesome-icon :icon="['far', 'save']"></font-awesome-icon>-->
                            <!--                            <span class="ml-2">Changes Saved</span>-->
                            <!--                        </v-chip>-->
                        </div>
                    </v-app-bar>
                </ion-toolbar>
            </ion-header>

            <IonContent>
                <v-main class="fill-height pt-0">
                    <!-- meta.fullBleed pages (Contacts) draw edge to edge, without the page gutter. -->
                    <v-container fluid class="fill-height align-baseline" :class="{ 'pa-0': vuetifyDisplay.smAndDown.value || route.meta?.fullBleed }">
                        <div style="width: inherit; height: inherit">
                            <router-view v-slot="{ Component }">
                                <transition name="fade" mode="out-in">
                                    <component :is="Component" />
                                </transition>
                            </router-view>
                        </div>
                        <!--Lower Notch Padding Protection-->
                        <br v-if="vuetifyDisplay.smAndDown.value" style="clear: both" />
                    </v-container>
                </v-main>
            </IonContent>

            <BnContactEditDialog v-if="!isPortal" ref="contactEditDialog" :auth-store="authStore" />

            <BnDialog ref="bnDialogRef" />

            <BnPerspectivesSetupDialog :model-value="showPerspectivesSetup" @update:model-value="onPerspectivesSetupUpdate" />

            <BnMyAccount ref="myAccountDialog" :is-portal="isPortal" :full-name="authStore.fullName" />
            <div class="d-none">{{ browserTitle }}</div>

            <!-- Telehealth host video dock (#5015): app-level so the call survives chart navigation -->
            <BnTelehealthDock v-if="!isPortal" />

            <!-- Lens panel (#6435): app-level so the conversation survives navigation -->
            <BnLensPanel v-if="!isPortal && featureEnabled('lens-panel')" :top="topBarHeight" />

            <!-- ios ample touch footer bar -->
            <BnFooter></BnFooter>
        </div>
    </IonPage>
</template>

<style lang="scss">
@use '@/assets/scss/bn.scss';

.ml-80 {
    margin-left: 80px;
}

.openMainDrawer {
    left: 0 !important;
}

.openSecondaryDrawer {
    left: 80px !important;
}

.border-left {
    border-left: 2px solid #5867dd;
}

/* Hacky fixes to get Ionic and Vuetify to work together */
ion-header {
    z-index: 110;
}

.v-app-bar {
    position: relative !important;
}
/* End Hacky Fixes */
</style>
